import { c as _c } from "react/compiler-runtime";
import { IcoArrowUpRight16 } from '@onefootprint/icons';
import { MarkdownText } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CompleteFormData, CtaSlot, PreviewSelection } from '../../complete-node.types';
import { willRenderCta } from '../../will-render-cta';
import { PreviewSelectable } from './preview-selectable';
type PreviewSelectionProps = {
  selected: PreviewSelection | null;
  onSelect: (item: PreviewSelection) => void;
  onRemove: (slot: CtaSlot) => (() => void) | undefined;
};
type CompleteNodePreviewProps = {
  /** Absent in read only, where the preview is only a picture of the screen. */
  selection?: PreviewSelectionProps;
};

// Spans with Button's own classes and data attributes rather than Buttons: each sits inside the
// button that selects it.
const PreviewButton = t0 => {
  const $ = _c(11);
  const {
    text,
    variant,
    newTab
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <span className="overflow-hidden text-ellipsis whitespace-nowrap">{text}</span>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== newTab || $[3] !== variant) {
    t2 = newTab && <IcoArrowUpRight16 color={variant === "primary" ? "quinary" : "primary"} />;
    $[2] = newTab;
    $[3] = variant;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="flex items-center justify-center gap-2">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3 || $[9] !== variant) {
    t4 = <span className="fp-button fp-custom-appearance text-label-2" data-variant={variant} data-size="large" data-full-width={true}>{t3}</span>;
    $[8] = t3;
    $[9] = variant;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const PreviewLink = t0 => {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <span className="fp-link-button fp-custom-appearance text-label-2 inline-flex items-center self-center w-fit gap-1 whitespace-nowrap align-middle" data-variant="label-2" data-icon-position="right"><span className="flex items-center h-full">{text}</span></span>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const CompleteNodePreview = t0 => {
  const $ = _c(41);
  const {
    selection
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["header", "subheader", "content", "ctaLabel", "ctaUrl", "ctaTarget", "secondaryCtaLabel", "secondaryCtaUrl", "secondaryCtaTarget", "secondaryCtaStyle", "autoAdvance", "autoAdvanceSecs"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [header, subheader, content, ctaLabel, ctaUrl, ctaTarget, secondaryCtaLabel, secondaryCtaUrl, secondaryCtaTarget, secondaryCtaStyle, autoAdvance, autoAdvanceSecs] = useWatch(t2);
  let t3;
  if ($[3] !== ctaLabel || $[4] !== ctaUrl) {
    t3 = willRenderCta(ctaLabel, ctaUrl);
    $[3] = ctaLabel;
    $[4] = ctaUrl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const showCta = t3;
  let t4;
  if ($[6] !== ctaUrl || $[7] !== secondaryCtaLabel || $[8] !== secondaryCtaUrl) {
    t4 = !!ctaUrl && willRenderCta(secondaryCtaLabel, secondaryCtaUrl);
    $[6] = ctaUrl;
    $[7] = secondaryCtaLabel;
    $[8] = secondaryCtaUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const showSecondaryCta = t4;
  let t5;
  if ($[10] !== autoAdvance || $[11] !== autoAdvanceSecs || $[12] !== t) {
    t5 = autoAdvance && autoAdvanceSecs != null ? t(_temp, {
      seconds: autoAdvanceSecs
    }) : undefined;
    $[10] = autoAdvance;
    $[11] = autoAdvanceSecs;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const caption = t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[14] !== caption || $[15] !== content || $[16] !== ctaLabel || $[17] !== ctaTarget || $[18] !== header || $[19] !== secondaryCtaLabel || $[20] !== secondaryCtaStyle || $[21] !== secondaryCtaTarget || $[22] !== selection || $[23] !== showCta || $[24] !== showSecondaryCta || $[25] !== subheader || $[26] !== t) {
    const removeLabels = {
      primary: t(_temp2),
      secondary: t(_temp3)
    };
    const removeFor = item => {
      if (item !== "primary" && item !== "secondary") {
        return;
      }
      const onRemove = selection?.onRemove(item);
      return onRemove ? {
        label: removeLabels[item],
        onRemove
      } : undefined;
    };
    const selectable = (item_0, children, t10) => {
      const options = t10 === undefined ? {} : t10;
      return selection ? <PreviewSelectable isSelected={selection.selected === item_0} onSelect={() => selection.onSelect(item_0)} remove={removeFor(item_0)} {...options}>{children}</PreviewSelectable> : children;
    };
    t6 = "page-shell flex flex-col gap-6 flex-1";
    let t11;
    if ($[31] !== header || $[32] !== subheader || $[33] !== t) {
      t11 = header ? <span className="page-shell__header fp-custom-appearance fp-page-header flex flex-col gap-2 items-center"><span className="fp-custom-appearance fp-page-header-title text-heading-3 text-primary">{header}</span>{subheader && <span className="fp-custom-appearance fp-page-header-subtitle text-body-2 text-secondary text-center">{subheader}</span>}</span> : <span className="flex flex-col gap-2 items-center text-center"><span className="text-heading-3 text-tertiary">{t(_temp4)}</span><span className="text-body-2 text-tertiary">{t(_temp5)}</span></span>;
      $[31] = header;
      $[32] = subheader;
      $[33] = t;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = {
        wide: true
      };
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    t7 = selectable("header", t11, t12);
    t8 = content && selectable("content", <div inert={true} className="page-shell__content flex-1"><MarkdownText className="text-body-2 text-primary">{content}</MarkdownText></div>, {
      label: t(_temp6),
      wide: true
    });
    t9 = (showCta || showSecondaryCta || caption) && <div className="page-shell__cta flex flex-col gap-3">{showCta && selectable("primary", <PreviewButton text={ctaLabel} variant="primary" newTab={ctaTarget === "new_tab"} />)}{showSecondaryCta && selectable("secondary", secondaryCtaStyle === "link" ? <PreviewLink text={secondaryCtaLabel} /> : <PreviewButton text={secondaryCtaLabel} variant="secondary" newTab={secondaryCtaTarget === "new_tab"} />)}{caption && <p className="page-shell__caption text-body-2 text-tertiary text-center">{caption}</p>}</div>;
    $[14] = caption;
    $[15] = content;
    $[16] = ctaLabel;
    $[17] = ctaTarget;
    $[18] = header;
    $[19] = secondaryCtaLabel;
    $[20] = secondaryCtaStyle;
    $[21] = secondaryCtaTarget;
    $[22] = selection;
    $[23] = showCta;
    $[24] = showSecondaryCta;
    $[25] = subheader;
    $[26] = t;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
    t9 = $[30];
  }
  let t10;
  if ($[36] !== t6 || $[37] !== t7 || $[38] !== t8 || $[39] !== t9) {
    t10 = <div className={t6}>{t7}{t8}{t9}</div>;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
function _temp($) {
  return $.completeNode.preview.autoAdvanceCaption;
}
function _temp2($_0) {
  return $_0.completeNode.link.remove;
}
function _temp3($_1) {
  return $_1.completeNode.secondaryLink.remove;
}
function _temp4($_2) {
  return $_2.nodePreview.headerPlaceholder;
}
function _temp5($_3) {
  return $_3.nodePreview.subheaderPlaceholder;
}
function _temp6($_4) {
  return $_4.completeNode.preview.content;
}